package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.min
import co.uk.sacursoftware.ssinventory.data.model.ProcessSilentDeliveryRequest
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.AppSession
import co.uk.sacursoftware.ssinventory.util.FirebaseStorageService
import co.uk.sacursoftware.ssinventory.util.StorageArea
import co.uk.sacursoftware.ssinventory.util.rememberPlatformImagePicker
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import ssinventory.shared.generated.resources.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HomeScreen(
    onPrepClick: () -> Unit,
    onStockAndOrdersClick: () -> Unit,
    onReportsClick: () -> Unit,
    onSuppliersClick: () -> Unit,
    onDailyClick: () -> Unit,
    onDeliveryClick: () -> Unit,
    onSettingsClick: (() -> Unit)? = null,
    onServicesClick: () -> Unit,
    onToolsClick: () -> Unit,
    onTrainingClick: () -> Unit,
    onWebUnlockClick: (() -> Unit)? = null
) {
    val scope = rememberCoroutineScope()
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    val storageService = remember { FirebaseStorageService() }

    val cameraPicker = rememberPlatformImagePicker { bytes, _ ->
        scope.launch {
            try {
                val url = storageService.uploadImage(bytes, StorageArea.DELIVERIES)
                repository.processSilentDelivery(ProcessSilentDeliveryRequest(imageUrl = url))
            } catch (_: Throwable) {
            }
        }
    }

    LaunchedEffect(Unit) {
        repository.getProfile().onSuccess { profileItems ->
            AppSession.userProfile = profileItems
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
    ) {
        BoxWithConstraints(
            modifier = Modifier.fillMaxSize()
        ) {
            val screenWidth = maxWidth
            val edgeMargin = 20.dp
            val buttonGap = 16.dp
            val buttonSize = ((screenWidth - edgeMargin * 2 - buttonGap * 2) / 3f)
                .coerceIn(88.dp, 112.dp)
            val logoWidth = min(screenWidth * 0.26f, 120.dp)

            Column(
                modifier = Modifier.fillMaxSize(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Top Row: Camera (left) and Settings (right) — full screen width
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(start = 12.dp, end = 12.dp, top = 8.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    IconButton(
                        onClick = { cameraPicker.takePhoto() },
                        modifier = Modifier
                            .size(44.dp)
                            .background(MaterialTheme.colorScheme.surfaceVariant, shape = RoundedCornerShape(22.dp))
                    ) {
                        Image(
                            painter = painterResource(Res.drawable.delivery),
                            contentDescription = "Quick Camera",
                            modifier = Modifier.size(24.dp).background(Color.Transparent),
                            contentScale = ContentScale.Fit,
                        )
                    }

                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                        if (onWebUnlockClick != null) {
                            IconButton(
                                onClick = onWebUnlockClick,
                                modifier = Modifier
                                    .size(44.dp)
                                    .background(MaterialTheme.colorScheme.surfaceVariant, shape = RoundedCornerShape(22.dp))
                            ) {
                                Text("KEY", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.onSurface)
                            }
                        }
                        if (onSettingsClick != null) {
                            IconButton(
                                onClick = onSettingsClick,
                                modifier = Modifier
                                    .height(44.dp)
                                    .widthIn(min = 44.dp)
                                    .background(MaterialTheme.colorScheme.surfaceVariant, shape = RoundedCornerShape(22.dp))
                            ) {
                                Text("ADMIN", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.onSurface)
                            }
                        }
                    }
                }

                Spacer(modifier = Modifier.weight(0.45f))

                // Logo positioned lower between top icons and buttons below
                Card(
                    modifier = Modifier
                        .width(logoWidth),
                    shape = RoundedCornerShape(16.dp),
                    elevation = CardDefaults.cardElevation(defaultElevation = 8.dp),
                    colors = CardDefaults.cardColors(containerColor = Color.White)
                ) {
                    Image(
                        painter = painterResource(Res.drawable.asset_4),
                        contentDescription = "Logo",
                        modifier = Modifier
                            .fillMaxWidth(),
                        contentScale = ContentScale.Fit
                    )
                }
                
                Spacer(modifier = Modifier.weight(0.55f))

                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = edgeMargin),
                    contentAlignment = Alignment.Center
                ) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        // Row 1: Prep, Routine, Deliveries
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(buttonGap, Alignment.CenterHorizontally),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            HomeButton(
                                text = "Prep",
                                onClick = onPrepClick,
                                buttonSize = buttonSize
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.prep),
                                    contentDescription = "Prep",
                                    modifier = Modifier.size(buttonSize * 0.42f),
                                    contentScale = ContentScale.Fit
                                )
                            }
                            HomeButton(
                                text = "Routine",
                                onClick = onDailyClick,
                                buttonSize = buttonSize
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.daily),
                                    contentDescription = "Routine",
                                    modifier = Modifier.size(buttonSize * 0.42f),
                                    contentScale = ContentScale.Fit
                                )
                            }
                            HomeButton(
                                text = "Deliveries",
                                onClick = onDeliveryClick,
                                buttonSize = buttonSize
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.delivery),
                                    contentDescription = "Deliveries",
                                    modifier = Modifier.size(buttonSize * 0.42f),
                                    contentScale = ContentScale.Fit
                                )
                            }
                        }
                        // Row 2: S&O, Services, Tools
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(buttonGap, Alignment.CenterHorizontally),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            HomeButton(
                                text = "S&O",
                                onClick = onStockAndOrdersClick,
                                buttonSize = buttonSize
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.stock),
                                    contentDescription = "S&O",
                                    modifier = Modifier.size(buttonSize * 0.42f),
                                    contentScale = ContentScale.Fit
                                )
                            }
                            HomeButton(
                                text = "Services",
                                onClick = onServicesClick,
                                buttonSize = buttonSize
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.services),
                                    contentDescription = "Services",
                                    modifier = Modifier.size(buttonSize * 0.42f),
                                    contentScale = ContentScale.Fit
                                )
                            }
                            HomeButton(
                                text = "Tools",
                                onClick = onToolsClick,
                                buttonSize = buttonSize
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.tools),
                                    contentDescription = "Tools",
                                    modifier = Modifier.size(buttonSize * 0.42f),
                                    contentScale = ContentScale.Fit
                                )
                            }
                        }
                        // Row 3: Reports, Suppliers, Training
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(buttonGap, Alignment.CenterHorizontally),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            HomeButton(
                                text = "Reports",
                                onClick = onReportsClick,
                                buttonSize = buttonSize
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.reports),
                                    contentDescription = "Reports",
                                    modifier = Modifier.size(buttonSize * 0.42f),
                                    contentScale = ContentScale.Fit
                                )
                            }
                            HomeButton(
                                text = "Suppliers",
                                onClick = onSuppliersClick,
                                buttonSize = buttonSize
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.suppliers),
                                    contentDescription = "Suppliers",
                                    modifier = Modifier.size(buttonSize * 0.42f),
                                    contentScale = ContentScale.Fit
                                )
                            }
                            HomeButton(
                                text = "Training",
                                onClick = onTrainingClick,
                                buttonSize = buttonSize
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.training),
                                    contentDescription = "Training",
                                    modifier = Modifier.size(buttonSize * 0.42f),
                                    contentScale = ContentScale.Fit
                                )
                            }
                        }
                    }
                }

                Spacer(modifier = Modifier.weight(0.5f))
            }
        }
    }
}

@Composable
fun HomeButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    buttonSize: androidx.compose.ui.unit.Dp,
    iconContent: @Composable () -> Unit
) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = modifier
    ) {
        Card(
            onClick = onClick,
            modifier = Modifier.size(buttonSize),
            shape = RoundedCornerShape(16.dp),
            colors = CardDefaults.cardColors(
                containerColor = MaterialTheme.colorScheme.surface
            ),
            border = BorderStroke(1.5.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.2f)),
            elevation = CardDefaults.cardElevation(
                defaultElevation = 4.dp,
                pressedElevation = 8.dp,
                hoveredElevation = 6.dp
            )
        ) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                iconContent()
            }
        }
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            text = text,
            style = MaterialTheme.typography.bodySmall.copy(
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onBackground
            ),
            textAlign = TextAlign.Center,
            maxLines = 1
        )
    }
}

@Composable
fun SettingsGearIcon(modifier: Modifier = Modifier) {
    val color = MaterialTheme.colorScheme.onSurface
    Canvas(modifier = modifier) {
        val w = size.width
        val h = size.height
        val center = Offset(w / 2f, h / 2f)
        drawCircle(color, radius = w * 0.38f, center = center, style = androidx.compose.ui.graphics.drawscope.Stroke(width = 4f))
        drawCircle(color, radius = w * 0.16f, center = center)
    }
}

@Composable
fun BackArrowIcon(modifier: Modifier = Modifier.size(24.dp)) {
    val color = MaterialTheme.colorScheme.onSurface
    Canvas(modifier = modifier) {
        val stroke = size.minDimension * 0.11f
        val pad = size.minDimension * 0.22f
        val midY = size.height / 2f
        val tipX = pad
        val armX = size.width * 0.64f
        drawLine(color, Offset(armX, pad), Offset(tipX, midY), stroke, StrokeCap.Round)
        drawLine(color, Offset(tipX, midY), Offset(armX, size.height - pad), stroke, StrokeCap.Round)
    }
}

@Composable
fun CloseIcon(
    modifier: Modifier = Modifier.size(20.dp),
    tint: Color = MaterialTheme.colorScheme.onSurface
) {
    Canvas(modifier = modifier) {
        val stroke = size.minDimension * 0.12f
        val pad = size.minDimension * 0.22f
        drawLine(tint, Offset(pad, pad), Offset(size.width - pad, size.height - pad), stroke, StrokeCap.Round)
        drawLine(tint, Offset(size.width - pad, pad), Offset(pad, size.height - pad), stroke, StrokeCap.Round)
    }
}

@Composable
fun ScreenTitle(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.titleMedium,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis
    )
}

data class OverflowAction(
    val label: String,
    val destructive: Boolean = false,
    val onClick: () -> Unit
)

@Composable
fun OverflowDotsIcon(modifier: Modifier = Modifier.size(24.dp)) {
    val color = MaterialTheme.colorScheme.onSurface
    Canvas(modifier = modifier) {
        val r = size.minDimension * 0.09f
        val cx = size.width / 2f
        val gap = size.height / 4f
        drawCircle(color, radius = r, center = Offset(cx, gap))
        drawCircle(color, radius = r, center = Offset(cx, size.height / 2f))
        drawCircle(color, radius = r, center = Offset(cx, size.height - gap))
    }
}

@Composable
fun ConfirmDeleteDialog(
    message: String,
    title: String = "Delete?",
    confirmLabel: String = "Delete",
    onConfirm: () -> Unit,
    onDismiss: () -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(title) },
        text = { Text(message) },
        confirmButton = {
            TextButton(onClick = onConfirm) {
                Text(confirmLabel, color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Bold)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cancel") }
        }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun OverflowActionsMenu(actions: List<OverflowAction>) {
    if (actions.isEmpty()) return
    if (actions.size == 1) {
        val action = actions.first()
        TextButton(onClick = action.onClick) {
            Text(
                action.label.uppercase(),
                fontWeight = FontWeight.Bold,
                color = if (action.destructive) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurface,
                maxLines = 1
            )
        }
        return
    }
    var showSheet by remember { mutableStateOf(false) }
    IconButton(onClick = { showSheet = true }) {
        OverflowDotsIcon()
    }
    if (showSheet) {
        ModalBottomSheet(
            onDismissRequest = { showSheet = false },
            containerColor = MaterialTheme.colorScheme.surface,
            contentColor = MaterialTheme.colorScheme.onSurface
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().padding(start = 8.dp, end = 8.dp, bottom = 32.dp)
            ) {
                Text(
                    "Actions",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp)
                )
                actions.forEach { action ->
                    Surface(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 8.dp, vertical = 4.dp)
                            .clickable {
                                showSheet = false
                                action.onClick()
                            },
                        shape = MaterialTheme.shapes.medium,
                        color = MaterialTheme.colorScheme.surfaceVariant
                    ) {
                        Text(
                            action.label,
                            fontWeight = FontWeight.SemiBold,
                            style = MaterialTheme.typography.titleMedium,
                            color = if (action.destructive) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurface,
                            modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp)
                        )
                    }
                }
            }
        }
    }
}
